import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelCheckbox as _vModelCheckbox, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "search-bar" }
const _hoisted_2 = { class: "tags" }
const _hoisted_3 = { class: "system-tags" }
const _hoisted_4 = ["id", "onUpdate:modelValue", "onChange", "disabled"]
const _hoisted_5 = ["for"]
const _hoisted_6 = { class: "tag-name ib ml10" }
const _hoisted_7 = {
  key: 0,
  class: "font-sm"
}
const _hoisted_8 = {
  key: 1,
  class: "font-sm"
}
const _hoisted_9 = { class: "user-tags" }
const _hoisted_10 = ["id", "onUpdate:modelValue", "onChange"]
const _hoisted_11 = ["for"]
const _hoisted_12 = { class: "tag-name ib ml10" }
const _hoisted_13 = {
  key: 0,
  class: "font-sm"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = {
  key: 0,
  class: "tag-form"
}
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "errors" }

import { computed, onMounted, reactive, ref } from 'vue';
import tagApi from 'src/apis/tag';
import {
  TAG_TYPE1_SYSTEM,
  TAG_TYPE1_USER,
  TAG_NAME_CHOUKI_HOZON,
} from 'src/consts/tag';
import type { TagItem } from 'src/models/tag';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';

type NewTag = {
  id: number;
  tagName: string;
  isNew: boolean;
  isSelected: boolean;
  isPartiallySelected: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'TagModal',
  props: /*@__PURE__*/_mergeModels({
    title: {},
    allowCreateTag: { type: Boolean, default: true },
    isChoukiHozonTagDisabled: { type: Boolean, default: false }
  }, {
    "modelValue": { default: () => [] },
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["dismiss", "close"], ["update:modelValue"]),
  setup(__props: any, { emit: __emit }) {



const emit = __emit;

const selectedTagsModel = _useModel<TagItem[]>(__props, "modelValue");

const isTagItem = (tag: TagItem | NewTag): tag is TagItem => !!(tag as TagItem).tagType1;

const tags = reactive<Array<TagItem | NewTag>>([]);
const search = reactive({ tagName: '' });
const tagNameToAdd = ref('');
const errorMsgs = ref<string[]>([]);

const filteredTags = computed<(TagItem | NewTag)[]>(() =>
  tags.filter(e => e.tagName.includes(search.tagName)),
);
const systemTags = computed<(TagItem | NewTag)[]>(() =>
  filteredTags.value.filter(e => isTagItem(e) && e.tagType1 === TAG_TYPE1_SYSTEM),
);
const userTags = computed<(TagItem | NewTag)[]>(() =>
  filteredTags.value.filter(e => isTagItem(e) ? e.tagType1 === TAG_TYPE1_USER : e.isNew),
);

const isChoukiHozonTag = (tag: TagItem | NewTag): boolean =>
  tag.tagName === TAG_NAME_CHOUKI_HOZON;

const selectedStatus = (tag: TagItem | NewTag): string => {
  const { count, numMovies } = selectedTagsModel.value.find(e => e.id === tag.id) || {};
  return `${count}/${numMovies}の動画で選択中`;
};

const halfWidthLetterCount = (str: string): number => {
  let ret = 0;
  for (let i = 0, len = str.length; i < len; i++) {
    const asciiCode = str.charCodeAt(i);
    ret += asciiCode < 128 ? 1 : 2;
  }
  return ret;
};

const isValidTagName = (): boolean => {
  errorMsgs.value = [];
  const tagName = tagNameToAdd.value;
  if (!tagName) errorMsgs.value.push('動画タグは必須です');
  if (halfWidthLetterCount(tagName) > 32) errorMsgs.value.push('動画タグは全角16文字以内で入力してください');
  if (tags.find(e => e.tagName === tagName)) errorMsgs.value.push('その動画タグは既に使用されています');
  return !errorMsgs.value.length;
};

const addTag = (): void => {
  if (!isValidTagName()) return;

  const nextId = tags.length ? tags[tags.length - 1].id + 1 : 0;
  const obj: NewTag = {
    id: nextId,
    tagName: tagNameToAdd.value,
    isNew: true,
    isSelected: false,
    isPartiallySelected: false,
  };
  tags.push(obj);
  tagNameToAdd.value = '';
};

const removeTag = (id: number): void => {
  const idx = tags.findIndex(e => e.id === id);
  tags.splice(idx, 1);
};

const onClose = async(): Promise<void> => {
  try {
    errorMsgs.value = [];
    const reqs: [number, TagItem][] = await Promise.all(
      tags.map((tag, i) => [tag, i] as const)
        .filter(([tag]) => tag.isNew)
        .map(async([tag, i]): Promise<[number, TagItem]> => {
          const createdTag = await tagApi.create({ tag_name: tag.tagName });
          return [i, { ...createdTag, isSelected: false, isPartiallySelected: false, isNew: false }];
        }),
    );
    for (const [i, tag] of reqs) {
      tag.isSelected = tags[i].isSelected;
      tag.isPartiallySelected = tags[i].isPartiallySelected;
      tags[i] = tag;
    }
    const selectedTags = tags.filter((e): e is TagItem => e.isSelected && isTagItem(e)).map(e => ({ ...e }));
    selectedTagsModel.value = selectedTags;
    emit('close', selectedTags);
  } catch (e) {
    errorMsgs.value = getErrorMessages(e as AxiosError);
  }
};

const getTags = async(): Promise<void> => {
  const fetchedTags = await tagApi.index();
  const selectedTagMap = selectedTagsModel.value.reduce(
    (acc: Record<number, TagItem>, e) => { acc[e.id] = e; return acc; },
    {},
  );
  const tagItems: TagItem[] = fetchedTags.map(tag => {
    const selectedTag = selectedTagMap[tag.id];
    if (!selectedTag) {
      return { ...tag, isSelected: false, isPartiallySelected: false, isNew: false };
    }

    return {
      ...tag,
      isSelected: true,
      isPartiallySelected: selectedTag.isPartiallySelected,
      isNew: false,
    };
  });
  tags.splice(0, tags.length, ...tagItems.toSorted((a, b) => a.id - b.id));
};

onMounted(getTags);

return (_ctx: any,_cache: any) => {
  const _component_modal = _resolveComponent("modal")!

  return (!!tags)
    ? (_openBlock(), _createBlock(_component_modal, {
        key: 0,
        class: "tag-modal",
        title: __props.title,
        onClose: onClose,
        onDismiss: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('dismiss')))
      }, {
        body: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _withDirectives(_createElementVNode("input", {
              type: "text",
              class: "form-control",
              placeholder: "絞り込みキーワードを入力してください。",
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search.tagName) = $event))
            }, null, 512), [
              [_vModelText, search.tagName]
            ])
          ]),
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(systemTags.value, (e, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: _normalizeClass(["custom-control custom-checkbox", { 'disabled': isChoukiHozonTag(e) && __props.isChoukiHozonTagDisabled }]),
                  key: i
                }, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    class: "custom-control-input",
                    id: 'system-tag' + e.id,
                    "onUpdate:modelValue": ($event: any) => ((e.isSelected) = $event),
                    onChange: ($event: any) => (e.isPartiallySelected = false),
                    disabled: isChoukiHozonTag(e) && __props.isChoukiHozonTagDisabled
                  }, null, 40, _hoisted_4), [
                    [_vModelCheckbox, e.isSelected]
                  ]),
                  _createElementVNode("label", {
                    class: "custom-control-label",
                    for: 'system-tag' + e.id
                  }, [
                    _createElementVNode("div", _hoisted_6, [
                      _cache[3] || (_cache[3] = _createElementVNode("i", { class: "fas fa-circle fa-xs text-warning mr4" }, null, -1)),
                      _createTextVNode(_toDisplayString(e.tagName) + " ", 1),
                      (e.isPartiallySelected)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_7, "(" + _toDisplayString(selectedStatus(e)) + ")", 1))
                        : _createCommentVNode("", true),
                      (isChoukiHozonTag(e) && __props.isChoukiHozonTagDisabled)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, " ※ 長期保存タグは動画再生画面で設定してください "))
                        : _createCommentVNode("", true)
                    ])
                  ], 8, _hoisted_5)
                ], 2))
              }), 128))
            ]),
            _createElementVNode("div", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userTags.value, (e, i) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "custom-control custom-checkbox",
                  key: i
                }, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    class: "custom-control-input",
                    id: 'user-tag' + e.id,
                    "onUpdate:modelValue": ($event: any) => ((e.isSelected) = $event),
                    onChange: ($event: any) => (e.isPartiallySelected = false)
                  }, null, 40, _hoisted_10), [
                    [_vModelCheckbox, e.isSelected]
                  ]),
                  _createElementVNode("label", {
                    class: "custom-control-label",
                    for: 'user-tag' + e.id
                  }, [
                    _createElementVNode("div", _hoisted_12, [
                      _cache[4] || (_cache[4] = _createElementVNode("i", { class: "fas fa-circle fa-xs mr4" }, null, -1)),
                      _createTextVNode(_toDisplayString(e.tagName) + " ", 1),
                      (e.isPartiallySelected)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_13, "(" + _toDisplayString(selectedStatus(e)) + ")", 1))
                        : _createCommentVNode("", true)
                    ])
                  ], 8, _hoisted_11),
                  (e.isNew)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "del-button ib ml10",
                        onClick: ($event: any) => (removeTag(e.id))
                      }, [...(_cache[5] || (_cache[5] = [
                        _createElementVNode("i", { class: "far fa-times-circle" }, null, -1)
                      ]))], 8, _hoisted_14))
                    : _createCommentVNode("", true)
                ]))
              }), 128))
            ])
          ]),
          (__props.allowCreateTag)
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                _withDirectives(_createElementVNode("input", {
                  type: "text",
                  class: "form-control",
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((tagNameToAdd).value = $event))
                }, null, 512), [
                  [_vModelText, tagNameToAdd.value]
                ]),
                _createElementVNode("button", {
                  class: "btn btn-secondary",
                  onClick: addTag,
                  disabled: !tagNameToAdd.value
                }, " タグを追加 ", 8, _hoisted_16)
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_17, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errorMsgs.value, (msg, idx) => {
              return (_openBlock(), _createElementBlock("div", { key: idx }, _toDisplayString(msg), 1))
            }), 128))
          ])
        ]),
        _: 1
      }, 8, ["title"]))
    : _createCommentVNode("", true)
}
}

})